import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Container from '../components/ui/Container';
import { useQuiz } from '../hooks/useQuiz';
import Loader from '../components/Loader';
import Error from '../components/Error';
import StartScreen from '../features/quiz/StartScreen';
import Question from '../features/quiz/Question';
import Progress from '../features/quiz/Progress';
import FinishScreen from '../features/quiz/FinishScreen';
import EmailForm from '../features/quiz/EmailForm';
import Footer from '../components/Footer';
import NextButton from '../features/quiz/NextButton';
import { startQuiz, nextQuestion, finishQuiz, newAnswer, restartQuiz, collectEmail } from '../features/quiz/quizSlice';
import { QuizPageProps, UserData } from '../types';
import { AppDispatch } from '../store';
import { clearQuizData, saveUserData } from '../utils/localStorage';

const QuizContainer = styled(Container)`
  width: 100%;
  max-width: 80rem;
`;

const QuizPage: React.FC<QuizPageProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    status,
    activeQuestion,
    index,
    numQuestions,
    categoryCounts,
    answer,
    answers,
    questions,
    resultsSubmitted
  } = useQuiz();

  function handleStart(): void {
    dispatch(startQuiz());
  }

  function handleNewAnswer(index: number): void {
    dispatch(newAnswer(index));
  }

  function handleNextQuestion(): void {
    dispatch(nextQuestion());
  }

  function handleFinish(): void {
    dispatch(finishQuiz());
  }

  function handleRestart(): void {
    // Clear localStorage data when restarting
    clearQuizData();
    dispatch(restartQuiz());
  }

  function handleEmailSubmit(userData: UserData): void {
    saveUserData(userData);
    dispatch(collectEmail());
  }

  return (
    <QuizContainer>
      {status === 'loading' && <Loader />}
      {status === 'error' && <Error />}
      {status === 'ready' && (
        <StartScreen numQuestions={numQuestions} onStart={handleStart} />
      )}
      {status === 'active' && activeQuestion && (
        <>
          <Progress
            index={index}
            numQuestions={numQuestions}
            answer={answer}
          />
          <Question
            question={activeQuestion}
            onNewAnswer={handleNewAnswer}
            answer={answer}
          />
          <Footer>
            <NextButton
              answer={answer}
              numQuestions={numQuestions}
              index={index}
              onNextQuestion={handleNextQuestion}
              onFinish={handleFinish}
            />
          </Footer>
        </>
      )}
      {status === 'email-collection' && (
        <EmailForm
          onSubmit={handleEmailSubmit}
          onRestart={handleRestart}
          categoryCounts={categoryCounts}
          answers={answers}
          questions={questions}
        />
      )}
      {status === 'finished' && (
        <FinishScreen
          categoryCounts={categoryCounts}
          answers={answers}
          questions={questions}
          resultsSubmitted={resultsSubmitted}
          onRestart={handleRestart}
        />
      )}
    </QuizContainer>
  );
};

export default QuizPage;
